/** Consent storage for public-website analytics (PostHog + optional GA4). */

export type AnalyticsConsent = 'accepted' | 'rejected' | null

const STORAGE_KEY = 'pp_cookie_consent'
const COOKIE_NAME = 'pp_analytics'
const CONSENT_EVENT = 'pp-analytics-consent'

export function getAnalyticsConsent(): AnalyticsConsent {
  if (typeof window === 'undefined') return null
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'accepted' || raw === 'rejected') return raw
  } catch {
    /* ignore */
  }
  // Legacy cookie used by GA4 bootstrap
  try {
    const m = document.cookie.match(/(?:^|;\s*)pp_analytics=([01])/)
    if (m?.[1] === '1') return 'accepted'
    if (m?.[1] === '0') return 'rejected'
  } catch {
    /* ignore */
  }
  return null
}

function writeCookie(value: '1' | '0') {
  const maxAge = 60 * 60 * 24 * 365
  document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${maxAge}; SameSite=Lax; Secure`
}

export function setAnalyticsConsent(value: 'accepted' | 'rejected'): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.setItem(STORAGE_KEY, value)
  } catch {
    /* ignore */
  }
  writeCookie(value === 'accepted' ? '1' : '0')
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: value }))
}

export function clearAnalyticsConsent(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    /* ignore */
  }
  document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Lax`
  window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: null }))
}

export function onAnalyticsConsentChange(handler: (v: AnalyticsConsent) => void): () => void {
  const listener = (e: Event) => {
    const detail = (e as CustomEvent).detail as AnalyticsConsent
    handler(detail ?? getAnalyticsConsent())
  }
  window.addEventListener(CONSENT_EVENT, listener)
  return () => window.removeEventListener(CONSENT_EVENT, listener)
}

export { CONSENT_EVENT, STORAGE_KEY, COOKIE_NAME }
